import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Alert,
    IconButton,
    Card,
    CardContent,
    Grid,
    Divider,
    Tooltip,
} from "@mui/material";
import {
    Close as CloseIcon,
    AccountBalanceWallet as WalletIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Stars as PointsIcon,
    CreditCard as CreditCardIcon,
    Cancel as CancelIcon,
    AdminPanelSettings as AdminIcon,
    Receipt as ReceiptIcon,
    MonetizationOn as MoneyIcon,
} from "@mui/icons-material";
import MaterialTable from "material-table";
import { onValue, off, ref, get, getDatabase } from "firebase/database";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getBookingData } from "common/src/actions/bookinglistactions";
import { useNavigate } from "react-router-dom";
import { colors } from "./Theme/WebTheme";
import { firebase } from "common/src/config/configureFirebase";

const WalletHistoryModal = ({
    open,
    onClose,
    user,
    userId,
    withdrawAmount,
    userName,
    withdrawDate,
}) => {
    const { t } = useTranslation();
    const settings = useSelector((state) => state.settingsdata.settings);
    const [walletHistory, setWalletHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [powertranzOrderIds, setPowertranzOrderIds] = useState({});
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [userData, setUserData] = useState(null);
    const [lastApprovedWithdrawDate, setLastApprovedWithdrawDate] =
        useState(null);
    const [lastWithdrawAmount, setLastWithdrawAmount] = useState(null);

    const getUser = async (userId) => {
        const { singleUserRef } = firebase;
        const snapshot = await get(singleUserRef(userId));

        if (snapshot.exists()) {
            return snapshot.val();
        } else {
            return null;
        }
    };

    useEffect(() => {
        if (!user && userId) {
            getUser(userId)
                .then((userData) => {
                    if (userData) {
                        setUserData(userData);
                    }
                })
                .catch((error) => {
                    // Error loading user data
                });
        } else if (user && user.id) {
            setUserData(user);
        }
    }, [user, userId]);

    // Additional useEffect to ensure user data is loaded when modal opens
    useEffect(() => {
        if (open && userId && !userData) {
            setLoading(true);
            setError(null); // Clear any previous errors

            // Use direct Firebase reference instead of getUser function
            const database = getDatabase();
            const userRef = ref(database, `users/${userId}`);

            get(userRef)
                .then((snapshot) => {
                    if (snapshot.exists()) {
                        const loadedUserData = snapshot.val();
                        setUserData(loadedUserData);
                    } else {
                        setError("User not found");
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    setError("Failed to load user data: " + error.message);
                    setLoading(false);
                });
        }
    }, [open, userId, userData]);

    // Reset userData when modal is closed
    useEffect(() => {
        if (!open) {
            setUserData(null);
            setWalletHistory([]);
            setError(null);
        }
    }, [open]);

    useEffect(() => {
        // Determine the actual user ID to use
        const actualUserId = userId || (user && user.id);

        // Determine the mode based on available props
        const isFilteringMode = !!withdrawDate; // Has withdrawDate = filtering mode
        const isGeneralMode = !!user && !withdrawDate; // Has user object but no withdrawDate = general mode

        if (open && (userData || (user && actualUserId))) {
            if (isFilteringMode) {
                fetchLastApprovedWithdrawDate(actualUserId);
            } else if (isGeneralMode) {
                // For general wallet history, reset the filter date and fetch immediately
                setLastApprovedWithdrawDate(null);
                fetchWalletHistory(actualUserId);
                loadPowertranzOrderIds();
            } else {
                setLastApprovedWithdrawDate(null);
                fetchWalletHistory(actualUserId);
                loadPowertranzOrderIds();
            }
        }

        return () => {
            const actualUserId = userId || (user && user.id);
            if (actualUserId) {
                // Clean up listener when component unmounts or user changes
                const { walletHistoryRef } = firebase;
                off(walletHistoryRef(actualUserId));
            }
        };
    }, [open, userData, withdrawDate, userId, user]);

    // Function to calculate remaining balance after last withdrawal
    const calculateRemainingBalanceAfterLastWithdraw = (
        walletHistory,
        lastWithdrawDate,
        lastWithdrawAmount
    ) => {
        if (!walletHistory || !lastWithdrawDate) return 0;

        const lastWithdrawDateTime = new Date(
            typeof lastWithdrawDate === "string" &&
            lastWithdrawDate.match(/^\d+$/)
                ? parseInt(lastWithdrawDate)
                : lastWithdrawDate
        );

        // Calculate balance at the time of last withdrawal
        let balanceAtLastWithdraw = 0;

        walletHistory.forEach((transaction) => {
            const transactionDate = new Date(
                typeof transaction.date === "string" &&
                transaction.date.match(/^\d+$/)
                    ? parseInt(transaction.date)
                    : transaction.date || transaction.timestamp
            );

            // Only count transactions that happened before or at the last withdrawal
            if (transactionDate <= lastWithdrawDateTime) {
                const amount = parseFloat(transaction.amount) || 0;
                if (transaction.type === "Credit") {
                    balanceAtLastWithdraw += amount;
                } else if (transaction.type === "Debit") {
                    balanceAtLastWithdraw -= amount;
                }
            }
        });

        // Remaining balance = balance at last withdraw - last withdraw amount
        const remainingBalance =
            balanceAtLastWithdraw - (parseFloat(lastWithdrawAmount) || 0);

        return Math.max(0, remainingBalance); // Ensure non-negative
    };

    const fetchLastApprovedWithdrawDate = async (userId) => {
        try {
            const database = getDatabase();
            const withdrawsRef = ref(database, "withdraws");
            const snapshot = await get(withdrawsRef);

            if (snapshot.exists()) {
                const withdrawsData = snapshot.val();
                // Convert withdrawDate (ISO string format) to Date object
                const currentWithdrawDate = new Date(withdrawDate);

                const userWithdraws = Object.keys(withdrawsData)
                    .map((key) => ({ id: key, ...withdrawsData[key] }))
                    .filter((withdraw) => {
                        const isUserMatch =
                            withdraw.uid === userId ||
                            withdraw.requestedBy === userId;
                        const isApproved = withdraw.processed === "approved";

                        if (!isUserMatch || !isApproved) {
                            return false;
                        }

                        // Get the withdraw date - handle both ISO string and timestamp formats
                        const withdrawDateValue = withdraw.requestTimestamp;
                        const withdrawApprovedDate = new Date(
                            withdrawDateValue
                        );

                        // Only include withdrawals that were approved BEFORE the current withdrawDate
                        const isBeforeCurrentWithdraw =
                            withdrawApprovedDate < currentWithdrawDate;

                        return isBeforeCurrentWithdraw;
                    })
                    .sort((a, b) => {
                        const dateA = new Date(a.requestTimestamp);
                        const dateB = new Date(b.requestTimestamp);
                        return dateB - dateA; // Sort descending (newest first)
                    });

                if (userWithdraws.length > 0) {
                    // Get the most recent approved withdrawal that happened before current withdrawDate
                    const lastApproved = userWithdraws[0];
                    const lastDate = lastApproved.requestTimestamp;
                    const lastAmount =
                        lastApproved.amount || lastApproved.withdrawAmount || 0;

                    setLastApprovedWithdrawDate(lastDate);
                    setLastWithdrawAmount(lastAmount);
                } else {
                    setLastApprovedWithdrawDate(null);
                    setLastWithdrawAmount(null);
                }
            } else {
                setLastApprovedWithdrawDate(null);
                setLastWithdrawAmount(null);
            }

            // After setting the last approved withdraw date, fetch wallet history
            fetchWalletHistory(userId);
            loadPowertranzOrderIds();
        } catch (error) {
            setLastApprovedWithdrawDate(null);
            setLastWithdrawAmount(null);
            // Even if there's an error, still fetch wallet history
            fetchWalletHistory(userId);
            loadPowertranzOrderIds();
        }
    };

    const fetchWalletHistory = (userId) => {
        setLoading(true);
        setError(null);

        const { walletHistoryRef } = firebase;

        try {
            off(walletHistoryRef(userId)); // Clean up any existing listener

            onValue(
                walletHistoryRef(userId),
                (snapshot) => {
                    const data = snapshot.val();
                    if (data) {
                        let arr = Object.keys(data).map((i) => {
                            return {
                                id: i,
                                ...data[i],
                            };
                        });

                        // STEP 1: Sort data from newest to oldest FIRST before any filtering
                        arr.sort((a, b) => {
                            const dateA = parseTransactionDateTime(
                                a.date,
                                `initial_sort_${a.id}`
                            );
                            const dateB = parseTransactionDateTime(
                                b.date,
                                `initial_sort_${b.id}`
                            );

                            // Handle null dates (put them at the end)
                            if (!dateA && !dateB) return 0;
                            if (!dateA) return 1;
                            if (!dateB) return -1;

                            // Compare with precise time (milliseconds)
                            const timeA = dateA.getTime();
                            const timeB = dateB.getTime();

                            // If times are exactly the same, use transaction ID for consistent ordering
                            if (timeA === timeB) {
                                return (b.id || "").localeCompare(a.id || ""); // Reverse for newest first
                            }

                            return timeB - timeA; // Newest first for display
                        });

                        // STEP 2: Apply filtering only if withdrawDate is provided (withdrawal context)
                        if (withdrawDate) {
                            // Convert withdrawDate (ISO string format) to millisecond timestamp for comparison
                            const withdrawDateTime = new Date(withdrawDate);
                            const withdrawTimestamp =
                                withdrawDateTime.getTime();

                            // Apply requestTimestamp range filtering to get contributing transactions
                            // Range: previousWithdrawRequestTimestamp < transaction.date < currentWithdrawRequestTimestamp
                            // This shows only transactions that occurred between the two withdrawal requestTimestamps
                            const originalLength = arr.length;

                            arr = calculateWithdrawalBreakdown(
                                arr,
                                withdrawTimestamp,
                                lastApprovedWithdrawDate
                            );

                            // Note: Only transactions within the requestTimestamp range will be shown.
                        }
                        // For general wallet history (no withdrawDate), show all transactions without filtering

                        setWalletHistory(arr); // Already sorted, no need to reverse
                    } else {
                        setWalletHistory([]);
                    }
                    setLoading(false);
                },
                (error) => {
                    setError(t("Failed to load wallet history"));
                    setLoading(false);
                }
            );
        } catch (error) {
            setError(t("Failed to setup wallet history listener"));
            setLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return t("not_available");
        const date = new Date(timestamp);
        return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    };

    const formatAmount = (amount, type) => {
        if (!amount) return "0.00";
        const formattedAmount = parseFloat(amount).toFixed(2);
        const currencySymbol =
            settings && settings.symbol ? settings.symbol : "$";
        return type === "Credit"
            ? `+${currencySymbol}${formattedAmount}`
            : `-${currencySymbol}${formattedAmount}`;
    };

    const getTypeColor = (type) => {
        return type === "Credit" ? "success" : "error";
    };

    const loadPowertranzOrderIds = async () => {
        try {
            const db = getDatabase();
            const powertranzRef = ref(db, `powertranz_transactions`);
            const snapshot = await get(powertranzRef);
            const powertranzData = snapshot.val();

            if (powertranzData) {
                const orderIds = {};
                Object.keys(powertranzData).forEach((key) => {
                    if (powertranzData[key] && powertranzData[key].order_id) {
                        orderIds[key] = powertranzData[key].order_id;
                    }
                });
                setPowertranzOrderIds(orderIds);
            }
        } catch (error) {
            // Error fetching PowerTranz transactions
        }
    };

    const getBookingIdPowertranzTransaction = async (transaction_id) => {
        try {
            const db = getDatabase();
            const powertranzRef = ref(
                db,
                `powertranz_transactions/${transaction_id}`
            );
            const snapshot = await get(powertranzRef);
            const powertranzData = snapshot.val();
            if (powertranzData && powertranzData.order_id) {
                return powertranzData.order_id;
            }

            return null;
        } catch (error) {
            return null;
        }
    };

    const handleOpenBookingTransaction = async (transaction_id, rowData) => {
        // Cek apakah ini transaksi PowerTranz
        if (rowData && rowData.txRef === "Powertranz Payment") {
            try {
                // Ambil data powertranz_transaction untuk mendapatkan order_id
                const db = getDatabase();
                const powertranzRef = ref(
                    db,
                    `powertranz_transactions/${transaction_id}`
                );
                const snapshot = await get(powertranzRef);
                const powertranzData = snapshot.val();

                // console.log("PowerTranz transaction data:", powertranzData);

                if (powertranzData && powertranzData.order_id) {
                    // Jika order_id dimulai dengan WALLET, ini adalah top-up wallet
                    if (powertranzData.order_id.startsWith("WALLET-")) {
                        return;
                    } else {
                        // Ini adalah booking transaction, gunakan order_id sebagai booking_id
                        const bookingData = await dispatch(
                            getBookingData(powertranzData.order_id)
                        );
                        navigate("/trip-details", {
                            state: { tripData: bookingData },
                        });
                        return;
                    }
                }
            } catch (error) {
                // Error fetching PowerTranz transaction data
            }
        }

        // Jika bukan PowerTranz atau terjadi error, gunakan flow normal
        const bookingData = await dispatch(getBookingData(transaction_id));
        navigate("/trip-details", { state: { tripData: bookingData } });
    };

    // Calculate total credits and debits
    const calculateTotals = () => {
        const credits = walletHistory
            .filter((item) => item.type === "Credit")
            .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

        const debits = walletHistory
            .filter((item) => item.type === "Debit")
            .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

        return { credits, debits };
    };

    // Function to parse date/timestamp handling different formats
    const parseTransactionDateTime = (dateValue, context = "") => {
        if (!dateValue) {
            return null;
        }

        try {
            let parsedDate;

            // Handle different date formats
            if (typeof dateValue === "number") {
                // Already a timestamp - ensure it's in milliseconds
                const timestamp =
                    dateValue > 1000000000000 ? dateValue : dateValue * 1000;
                parsedDate = new Date(timestamp);
            } else if (typeof dateValue === "string") {
                // Check if it's a timestamp string (all digits)
                if (dateValue.match(/^\d+$/)) {
                    const timestamp = parseInt(dateValue);
                    // Handle both milliseconds and seconds timestamps
                    const millisecondTimestamp =
                        timestamp > 1000000000000
                            ? timestamp
                            : timestamp * 1000;
                    parsedDate = new Date(millisecondTimestamp);
                } else {
                    // Parse as ISO date string (e.g., "2025-08-25T19:51:48.378Z")
                    parsedDate = new Date(dateValue);
                }
            } else if (dateValue instanceof Date) {
                parsedDate = new Date(dateValue.getTime());
            } else {
                // Try direct conversion
                parsedDate = new Date(dateValue);
            }

            // Validate the parsed date
            if (isNaN(parsedDate.getTime())) {
                return null;
            }

            return parsedDate;
        } catch (error) {
            return null;
        }
    };

    // Function to calculate withdrawal breakdown - which transactions contributed to this withdrawal
    const calculateWithdrawalBreakdown = (
        allTransactions,
        currentWithdrawTimestamp = null,
        lastApprovedWithdrawTimestamp = null
    ) => {
        if (!allTransactions || allTransactions.length === 0) {
            return [];
        }

        // Data is already sorted from newest to oldest before calling this function
        // No need to sort again, just use the pre-sorted data

        // 1. Apply date range filtering if timestamps are provided
        let filteredTransactions = allTransactions;

        if (currentWithdrawTimestamp) {
            filteredTransactions = allTransactions.filter((transaction) => {
                const transactionDate = parseTransactionDateTime(
                    transaction.date,
                    `filter_${transaction.id}`
                );
                if (!transactionDate) return false;

                const transactionTimestamp = transactionDate.getTime();

                // Filter transactions within the date range
                if (lastApprovedWithdrawTimestamp) {
                    // Convert lastApprovedWithdrawTimestamp to milliseconds if it's a Date object
                    const lastTimestamp =
                        lastApprovedWithdrawTimestamp instanceof Date
                            ? lastApprovedWithdrawTimestamp.getTime()
                            : new Date(lastApprovedWithdrawTimestamp).getTime();

                    // Show transactions between last approved withdrawal and current withdrawal
                    return (
                        transactionTimestamp > lastTimestamp &&
                        transactionTimestamp < currentWithdrawTimestamp
                    );
                } else {
                    // Show all transactions before current withdrawal
                    return transactionTimestamp < currentWithdrawTimestamp;
                }
            });
        }

        console.log("filteredTransactions ======> ", filteredTransactions);

        // // 2. Find the first WTH (Withdraw) transaction in filtered results
        // const firstWthIndex = filteredTransactions.findIndex(
        //     (t) => t.type === "Withdraw"
        // );

        // if (firstWthIndex === -1) {
        //     // No withdraw transaction found, return all non-withdraw transactions
        //     return filteredTransactions.filter((t) => t.type !== "Withdraw");
        // }

        // // 3. Take all transactions BEFORE the first WTH (exclude WTH and everything after it)
        // // Since data is sorted newest first, transactions before WTH are from index 0 to firstWthIndex-1
        // const transactionsBeforeWth = filteredTransactions.slice(
        //     0,
        //     firstWthIndex
        // );

        const filteredWithdraw = filteredTransactions.filter(
            (item) => !item.txRef.startsWith("WTH")
        );

        const txRefCount = filteredWithdraw.reduce((acc, item) => {
            const ref = item.txRef;
            acc[ref] = (acc[ref] || 0) + 1;
            return acc;
        }, {});

        const cleanedTransactions = filteredWithdraw.filter((item) => {
            const ref = item.txRef;

            // Kalau meaningful → jangan dihapus walaupun duplikat
            if (isMeaningfulRef(ref)) {
                return true;
            }

            // Kalau bukan meaningful → hapus kalau muncul lebih dari 1x
            return txRefCount[ref] === 1;
        });

        return cleanedTransactions.filter((item) => item.type === "Credit");
    };

    function isMeaningfulRef(ref) {
        // Silakan tambah kata-kata lain sesuai kebutuhan kamu
        return /credit|admin|withdraw|cancel|refund|points|payment/i.test(ref);
    }

    const { credits, debits } = calculateTotals();

    const getTransactionIcon = (rowData) => {
        if (!rowData.txRef) return <ReceiptIcon fontSize="small" />;

        const txRef = rowData.txRef.toString();

        if (txRef === "Points Payment")
            return (
                <PointsIcon
                    sx={{ color: colors.MAIN_COLOR }}
                    fontSize="small"
                />
            );
        if (txRef.startsWith("WTH"))
            return (
                <MoneyIcon sx={{ color: colors.MAIN_COLOR }} fontSize="small" />
            );
        if (txRef.startsWith("WALLET"))
            return (
                <CreditCardIcon
                    sx={{ color: colors.MAIN_COLOR }}
                    fontSize="small"
                />
            );
        if (txRef === "Cancellation Fee")
            return (
                <CancelIcon
                    sx={{ color: colors.MAIN_COLOR }}
                    fontSize="small"
                />
            );
        if (
            txRef
                .toLowerCase()
                .replace(/\s+/g, "")
                .replace(/[^a-z0-9]/g, "") === "admincredit"
        )
            return (
                <AdminIcon sx={{ color: colors.MAIN_COLOR }} fontSize="small" />
            );
        if (txRef === "Powertranz Payment")
            return (
                <CreditCardIcon
                    sx={{ color: colors.MAIN_COLOR }}
                    fontSize="small"
                />
            );
        if (txRef.match(/^[A-Z0-9]{8,}$/))
            return (
                <ReceiptIcon
                    sx={{ color: colors.MAIN_COLOR }}
                    fontSize="small"
                />
            );

        return (
            <ReceiptIcon sx={{ color: colors.MAIN_COLOR }} fontSize="small" />
        );
    };

    const getDescription = (rowData) => {
        // Pastikan txRef ada
        if (!rowData.txRef) {
            return t("unknown_transaction");
        }

        const txRef = rowData.txRef.toString();

        // Pembayaran dengan poin
        if (txRef === "Points Payment") {
            return t("pay_with_points");
        }

        // Permintaan penarikan
        if (txRef.startsWith("WTH")) {
            return t("withdraw_request");
        }

        // Top up dengan PowerTranz
        if (txRef.startsWith("WALLET")) {
            return t("top_up_with_powertranz");
        }

        // Biaya pembatalan
        if (txRef === "Cancellation Fee") {
            return t("cancellation_fee");
        }

        // Kredit admin
        if (
            txRef
                .toLowerCase()
                .replace(/\s+/g, "")
                .replace(/[^a-z0-9]/g, "") === "admincredit"
        ) {
            return t("admin_credit");
        }

        // Pembayaran PowerTranz untuk booking
        if (txRef === "Powertranz Payment" && rowData.type === "Credit") {
            if (userData && userData.role === "user") {
                return t("top_up_with_powertranz_for_booking");
            } else {
                return t("top_up_with_powertranz");
            }
        }

        // Jika txRef adalah ID booking (biasanya format tertentu)
        if (txRef.match(/^[A-Z0-9]{8,}$/)) {
            return `${t("booking_transaction")} - ${txRef}`;
        }

        // Default: tampilkan txRef dengan format yang lebih baik
        return txRef
            .replace(/_/g, " ")
            .replace(/([A-Z])/g, " $1")
            .trim();
    };

    const columns = [
        {
            title: t("date"),
            field: "date",
            render: (rowData) => (
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.primary",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                    }}
                >
                    {formatDate(rowData.date)}
                </Typography>
            ),
            defaultSort: "desc",
        },
        {
            title: t("type"),
            field: "type",
            render: (rowData) => (
                <Chip
                    label={
                        rowData.type
                            ? t(rowData.type.toLowerCase())
                            : t("not_available")
                    }
                    color={getTypeColor(rowData.type)}
                    size="small"
                />
            ),
        },
        {
            title: t("amount"),
            field: "amount",
            style: {
                textAlign: "center",
            },
            render: (rowData) => {
                const isCredit = rowData.type === "Credit";
                const amount = formatAmount(rowData.amount, rowData.type);

                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            width: "100%",
                            margin: "auto",
                        }}
                    >
                        {isCredit ? (
                            <TrendingUpIcon
                                fontSize="small"
                                sx={{ color: "success.main" }}
                            />
                        ) : (
                            <TrendingDownIcon
                                fontSize="small"
                                sx={{ color: "error.main" }}
                            />
                        )}
                        <Typography
                            variant="body2"
                            color={isCredit ? "success.main" : "error.main"}
                            sx={{
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                fontFamily: "monospace",
                            }}
                        >
                            {amount}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            title: t("description"),
            field: "txRef",
            render: (rowData) => {
                // Daftar txRef yang tidak perlu ditampilkan sebagai button (hanya text)
                const excludedTxRefs = [
                    "AdminCredit",
                    "Cancellation Fee",
                    "Refund Credit",
                ];

                // Fungsi untuk menentukan apakah transaksi bisa diklik (memiliki booking terkait)
                const isClickableTransaction = () => {
                    // Tidak bisa diklik jika termasuk dalam daftar excluded
                    if (excludedTxRefs.includes(rowData.txRef)) {
                        return false;
                    }

                    // Tidak bisa diklik jika withdrawal request
                    if (rowData.txRef.startsWith("WTH")) {
                        return false;
                    }

                    if (
                        rowData.txRef
                            .toLowerCase()
                            .replace(/\s+/g, "")
                            .replace(/[^a-z0-9]/g, "") === "admincredit"
                    ) {
                        return false;
                    }

                    // Tidak bisa diklik jika Powertranz Payment tipe Debit (pembayaran booking)
                    if (
                        rowData.type === "Debit" &&
                        rowData.txRef === "Powertranz Payment"
                    ) {
                        return false;
                    }

                    // Tidak bisa diklik jika wallet top-up (dimulai dengan WALLET)
                    if (rowData.txRef.startsWith("WALLET")) {
                        return false;
                    }

                    // Tidak bisa diklik jika user adalah driver dan transaksi adalah Powertranz Payment Credit
                    if (
                        userData &&
                        userData.usertype === "driver" &&
                        rowData.type === "Credit" &&
                        rowData.txRef === "Powertranz Payment"
                    ) {
                        return false;
                    }

                    // Tidak bisa diklik jika Powertranz Payment tipe Credit tapi untuk wallet top-up
                    if (
                        rowData.type === "Credit" &&
                        rowData.txRef === "Powertranz Payment" &&
                        rowData.transaction_id
                    ) {
                        const orderId =
                            powertranzOrderIds[rowData.transaction_id];
                        if (orderId && orderId.startsWith("WALLET-")) {
                            return false;
                        }
                    }

                    return true;
                };

                const icon = getTransactionIcon(rowData);
                const description = getDescription(rowData);

                // Jika transaksi bisa diklik, tampilkan sebagai button dengan styling text biasa
                if (isClickableTransaction()) {
                    return (
                        <Button
                            onClick={() =>
                                handleOpenBookingTransaction(
                                    rowData.transaction_id || rowData.txRef,
                                    rowData
                                )
                            }
                            size="small"
                            variant="text"
                            startIcon={icon}
                            sx={{
                                textTransform: "none",
                                justifyContent: "flex-start",
                                minWidth: "auto",
                                padding: "6px 0",
                                color: colors.MAIN_COLOR,
                                fontWeight: 500,
                                fontSize: "0.875rem",
                                lineHeight: 1.4,
                                "&:hover": {
                                    backgroundColor: "transparent",
                                    color: "primary.main",
                                },
                                "& .MuiButton-startIcon": {
                                    color: "text.secondary",
                                    marginRight: "8px",
                                },
                            }}
                        >
                            {description}
                        </Button>
                    );
                }

                // Jika tidak bisa diklik, tampilkan sebagai text biasa
                return (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            padding: "6px 0",
                        }}
                    >
                        <Box sx={{ color: "text.secondary", display: "flex" }}>
                            {icon}
                        </Box>
                        <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                                fontWeight: 500,
                                lineHeight: 1.4,
                            }}
                        >
                            {description}
                        </Typography>
                    </Box>
                );
            },
        },
    ];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minHeight: "700px" },
            }}
        >
            <DialogTitle>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {user ? (
                        <>
                            <Box display="flex" alignItems="center" gap={1}>
                                <WalletIcon color="primary" />
                                <Typography variant="h6">
                                    {t("wallet")} - {userData?.firstName}{" "}
                                    {userData?.lastName}
                                </Typography>
                            </Box>
                            <IconButton onClick={onClose} size="small">
                                <CloseIcon />
                            </IconButton>
                        </>
                    ) : (
                        <IconButton
                            style={{
                                position: "absolute",
                                top: 16,
                                right: 16,
                            }}
                            onClick={onClose}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>
            </DialogTitle>

            <DialogContent>
                {userData && (
                    <Box mb={3}>
                        {/* User Info */}
                        <Typography
                            variant="body2"
                            color="textSecondary"
                            mb={2}
                        >
                            {t("user")}: {userData.firstName}{" "}
                            {userData.lastName} ({userData.email})
                        </Typography>

                        {user && (
                            <Grid container spacing={2} mb={3}>
                                <Grid item xs={12} md={4}>
                                    <Card
                                        sx={{
                                            background:
                                                "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                            color: "white",
                                            textAlign: "center",
                                            minHeight: "120px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CardContent>
                                            <WalletIcon
                                                sx={{ fontSize: 40, mb: 1 }}
                                            />
                                            <Typography
                                                variant="h6"
                                                component="div"
                                            >
                                                {t("current_balance")}
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                component="div"
                                                fontWeight="bold"
                                            >
                                                {settings && settings.symbol
                                                    ? settings.symbol
                                                    : "$"}
                                                {parseFloat(
                                                    userData.walletBalance || 0
                                                ).toFixed(2)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Card
                                        sx={{
                                            background:
                                                "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                                            color: "white",
                                            textAlign: "center",
                                            minHeight: "120px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CardContent>
                                            <TrendingUpIcon
                                                sx={{ fontSize: 40, mb: 1 }}
                                            />
                                            <Typography
                                                variant="h6"
                                                component="div"
                                            >
                                                {t("total_credits")}
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                component="div"
                                                fontWeight="bold"
                                            >
                                                {settings && settings.symbol
                                                    ? settings.symbol
                                                    : "$"}
                                                {credits.toFixed(2)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Card
                                        sx={{
                                            background:
                                                "linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)",
                                            color: "white",
                                            textAlign: "center",
                                            minHeight: "120px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CardContent>
                                            <TrendingDownIcon
                                                sx={{ fontSize: 40, mb: 1 }}
                                            />
                                            <Typography
                                                variant="h6"
                                                component="div"
                                            >
                                                {t("total_debits")}
                                            </Typography>
                                            <Typography
                                                variant="h4"
                                                component="div"
                                                fontWeight="bold"
                                            >
                                                {settings && settings.symbol
                                                    ? settings.symbol
                                                    : "$"}
                                                {debits.toFixed(2)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        )}

                        <Divider sx={{ mb: 2 }} />

                        {withdrawDate ? (
                            <Box mb={2}>
                                <Typography
                                    variant="h6"
                                    mb={1}
                                    display="flex"
                                    alignItems="center"
                                    gap={1}
                                >
                                    <ReceiptIcon color="primary" />
                                    {`Withdrawal Breakdown for ${userName}`}
                                </Typography>
                                <Card
                                    sx={{
                                        backgroundColor: "#f8f9fa",
                                        border: "1px solid #e9ecef",
                                    }}
                                >
                                    <CardContent sx={{ py: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Withdrawal Amount
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    color="primary"
                                                >
                                                    $
                                                    {withdrawAmount
                                                        ? withdrawAmount.toFixed(
                                                              2
                                                          )
                                                        : "0.00"}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Withdrawal Date
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    fontWeight="medium"
                                                >
                                                    {(() => {
                                                        const d =
                                                            parseTransactionDateTime(
                                                                withdrawDate,
                                                                "withdraw_header"
                                                            );
                                                        return d
                                                            ? d.toLocaleString()
                                                            : withdrawDate;
                                                    })()}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    Contributing Transactions
                                                </Typography>
                                                <Typography
                                                    variant="h6"
                                                    color="success.main"
                                                >
                                                    {walletHistory.length}{" "}
                                                    transactions
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{ fontStyle: "italic" }}
                                        >
                                            Showing transactions that
                                            contributed to this withdrawal
                                            request
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>
                        ) : (
                            <Typography
                                variant="h6"
                                mb={2}
                                display="flex"
                                alignItems="center"
                                gap={1}
                            >
                                <WalletIcon color="primary" />
                                {t("transaction_history")}
                            </Typography>
                        )}
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight="300px"
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {console.log("📊 DATA PASSED TO MaterialTable:", {
                            walletHistoryLength: walletHistory.length,
                            walletHistoryIds: walletHistory.map((t) => t.id),
                            walletHistoryTypes: walletHistory.map(
                                (t) => t.type
                            ),
                            walletHistoryAmounts: walletHistory.map(
                                (t) => t.amount
                            ),
                            firstThreeTransactions: walletHistory.slice(0, 3),
                            ALL_TRANSACTIONS_DETAILED: walletHistory.map(
                                (t, index) => ({
                                    index,
                                    id: t.id,
                                    type: t.type,
                                    amount: t.amount,
                                    rawDate: t.rawDate || t.date,
                                    parsedDate: t.parsedDate,
                                    timestamp: t.timestamp,
                                })
                            ),
                        })}
                        <MaterialTable
                            title=""
                            columns={columns}
                            data={walletHistory}
                            options={{
                                search: true,
                                paging: true,
                                pageSize: 10,
                                pageSizeOptions: [5, 10, 20, 50],
                                sorting: true,
                                exportButton: true,
                                exportAllData: true,
                                exportFileName: `wallet_history_${userData?.firstName}_${userData?.lastName}`,
                                headerStyle: {
                                    backgroundColor: "#f8f9fa",
                                    fontWeight: 600,
                                    fontSize: "0.875rem",
                                    color: "#495057",
                                    borderBottom: "2px solid #e9ecef",
                                },
                                rowStyle: (rowData) => ({
                                    fontSize: "0.875rem",
                                    backgroundColor:
                                        rowData.tableData.id % 2 === 0
                                            ? "#ffffff"
                                            : "#f8f9fa",
                                    "&:hover": {
                                        backgroundColor: "#e3f2fd !important",
                                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                    },
                                    transition: "all 0.2s ease-in-out",
                                }),
                                emptyRowsWhenPaging: false,
                                showEmptyDataSourceMessage: true,
                                emptyDataSourceMessage: t(
                                    "no_wallet_transactions"
                                ),
                                actionsColumnIndex: -1,
                                toolbar: true,
                                toolbarButtonAlignment: "right",
                            }}
                            localization={{
                                pagination: {
                                    labelDisplayedRows: t(
                                        "displayed_rows_template",
                                        {
                                            from: "{from}",
                                            to: "{to}",
                                            count: "{count}",
                                        }
                                    ),
                                    labelRowsSelect: t("rows"),
                                    labelRowsPerPage: t("rows_per_page"),
                                    firstAriaLabel: t("first_page"),
                                    firstTooltip: t("first_page"),
                                    previousAriaLabel: t("previous_page"),
                                    previousTooltip: t("previous_page"),
                                    nextAriaLabel: t("next_page"),
                                    nextTooltip: t("next_page"),
                                    lastAriaLabel: t("last_page"),
                                    lastTooltip: t("last_page"),
                                },
                                toolbar: {
                                    searchTooltip: t("search"),
                                    searchPlaceholder: t("search_transactions"),
                                    exportCSVName: t("export_csv"),
                                    exportPDFName: t("export_pdf"),
                                },
                                header: {
                                    actions: t("actions"),
                                },
                                body: {
                                    emptyDataSourceMessage: t(
                                        "no_wallet_transactions_for_user"
                                    ),
                                },
                            }}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    {t("close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WalletHistoryModal;
